import React from "react"
import styled, { css } from "styled-components"
import Img from "gatsby-image"
import Parser from "html-react-parser"
import Fade from "react-reveal/Fade"

import Section from "../elements/Section"
import Container from "../elements/Container"
import Button from "../elements/Button"
import { TitleH2 } from "../elements/Title"
import globalVariables from "../globalVariables"

const WrapperCards = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: ${globalVariables.minTablet}) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`
const getBackgroundColor = props => {
  let background

  switch (props.background) {
    case "Gris":
      background = "rgba(81, 85, 93, 0.75)"
      break
    case "Blanc":
      background = "rgba(217, 226, 235, 0.75)"
      break
    case "Bleu":
      background = "rgba(82, 145, 206, 0.8)"
      break
    default:
      background = ""
  }

  return css`
    background: ${background};
  `
}

const Card = styled.div`
  flex: 0 1 50%;
  @media (min-width: ${globalVariables.minDesktop}) {
    flex: 0 1 33.33%;
  }
`

const Space = styled.div`
  margin-bottom: 20px;
  position: relative;
  transition: all 0.2s ease-in-out;
  ${getBackgroundColor};
  img {
    z-index: -1;
  }
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  & > .gatsby-image-wrapper {
    height: 280px;
  }

  @media (min-width: ${globalVariables.minTablet}) {
    margin: 10px;
    & > .gatsby-image-wrapper {
      height: 300px;
    }
  }
`

const Content = styled.div`
  transition: all 0.1s ease-in-out;

  ${Card}:hover & {
    opacity: 0;
    transition: all 0.4s ease-in-out;
  }
`

const WrapperLogo = styled.div`
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  height: 90px;

  img {
    z-index: 1;
    object-fit: contain !important;
  }
  @media (min-width: ${globalVariables.minTablet}) {
    width: ${props => (props.width ? props.width : "150px")};
    height: 100px;
  }
`

const Tag = styled.span`
  text-transform: uppercase;
  color: white;
  background: ${props => props.theme.secondaryWithOpacity};
  padding: 8px 15px;
  border-radius: 8px;
  position: absolute;
  bottom: 25%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
`

const List = styled.ul`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-left: 0;
  background: ${props => props.theme.secondaryWithOpacity};
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  padding: 0 11px;
`

const Item = styled.li`
  color: white;
  list-style: none;
  font-size: 12px;
  margin-bottom: 0;
  text-align: center;
  padding: 0 11px;
`

const HoverContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 0;
  text-align: center;
  background: ${props => props.theme.secondaryWithOpacity80};
  transition: all 0.3s ease-in-out;

  flex-direction: column;
  justify-content: center;
  align-items: center;

  p,
  a {
    opacity: 0;
    transition: all 0.1s ease-in-out;
  }
  a {
    margin-bottom: 40px;
    /* margin-top: 10px; */
    height: 46px;
  }
  ${Card}:hover & {
    height: 100%;
    display: flex;
    p,
    a {
      opacity: 1;
      transition: all 0.5s ease-in-out;
    }
    a:hover {
      background: white;
      border-color: white;
      span {
        color: ${props => props.theme.primary};
      }
    }
  }
`

const Text = styled.div`
  p {
    text-align: center;
    color: white;
  }

  margin-bottom: 0;
  padding: 5px 1rem;

  @media (min-width: ${globalVariables.minTablet}) {
    padding: 5px 2rem;
  }
`

const WrapperButton = styled.div`
  text-align: center;
  margin-top: 25px;
  margin-bottom: 8px;
  @media (min-width: ${globalVariables.minTablet}) {
    margin-top: 65px;
    margin-bottom: 0;
  }
`

const ReferencesList = ({ items, title, buttonText, buttonUrl }) => {
  return (
    <Section>
      <Container>
        {title && (
          <TitleH2 position="center" className="title1" margin="0 0 55px 0">
            {title}
          </TitleH2>
        )}

        <WrapperCards>
          {items.map((item, index) => {
            const reference = item.node.acf
            return (
              <Card key={index}>
                <Fade left>
                  <Space background={reference.couleur_vignette_etude_de_cas}>
                    <Img
                      fluid={
                        reference.image_vignette_etude_de_cas.localFile
                          .childImageSharp.fluid
                      }
                    />
                    <Content>
                      <WrapperLogo width="165px">
                        <Img
                          fluid={
                            reference.logo_etude_de_cas.localFile
                              .childImageSharp.fluid
                          }
                        />
                      </WrapperLogo>
                      <Tag>{reference.secteur_banniere_etude_de_cas}</Tag>
                    </Content>
                    <HoverContent>
                      <Text>
                        {Parser(reference.texte_banniere_etude_de_cas)}
                      </Text>
                      {reference.creation_etude_de_cas === "Page" && (
                        <Button
                          colors="primaryWithBg"
                          as="link"
                          title="Découvrir l'étude de cas"
                          to={item.node.path}
                        />
                      )}
                    </HoverContent>
                    <List>
                      {reference.prestations.map((prestation, index) => (
                        <Item key={index}>
                          <span>{prestation.texte_prestation}</span>
                        </Item>
                      ))}
                    </List>
                  </Space>
                </Fade>
              </Card>
            )
          })}
        </WrapperCards>
        {buttonText && buttonUrl && (
          <WrapperButton>
            <Button
              to={buttonUrl}
              title={buttonText}
              colors="primaryWithBg"
              as="link"
            />
          </WrapperButton>
        )}
      </Container>
    </Section>
  )
}

export default ReferencesList
